import Toast from 'react-native-toast-message'
import { getCurrentOrgID } from '../data/organization';
import ErrorHandler from '../utils/ErrorHandler';
import findAssociativeChanges from '../utils/findAssociativeChanges';
import { queryClient, supabase } from '../data/client';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { insertActivity } from './activity';
import { buildUpdateDetails, joinDetails } from '../utils/buildActivityDetails';
import { PEOPLE_SELECT } from '../utils/peopleSelect';

// A hidden role is only visible to admins or users who own the role (canManage).
// `user.ManagingRoles` is an array of role objects the user can manage (see people.js).
export const isRoleVisibleToUser = (role, user) => {
    if (!role?.isHidden) return true;
    if (user?.isAdmin) return true;
    return user?.ManagingRoles?.some(r => r.id === role?.id) || false;
};

// Locked permissions are per-org: a permission is locked for the current org iff a row
// exists in OrganizationLockedPermissions(organizationID, permissionID). The global
// Permissions.locked column is dropped — fetch the org's locked IDs once and merge
// client-side so consumers keep the same `locked` boolean shape.
const LOCKED_PERMISSIONS_CACHE_TIME = 1000 * 60 * 15;
const lockedPermissionsByOrg = new Map();

const getLockedPermissions = async (orgID) => {
    if(!orgID) return {ids: new Set(), names: new Set()};

    const cached = lockedPermissionsByOrg.get(orgID);
    if(cached?.expiresAt > Date.now()) return cached.request;
    lockedPermissionsByOrg.delete(orgID);

    const request = (async () => {
        const {error, data} = await supabase.from('OrganizationLockedPermissions').select('permissionID').eq('organizationID', orgID);

        if(error){
            throw error;
        }

        const ids = new Set((data || []).map(row => row.permissionID));
        if(ids.size === 0) return {ids, names: new Set()};

        const {error: permissionsError, data: permissions} = await supabase.from('Permissions').select('name').in('id', [...ids]);
        if(permissionsError){
            throw permissionsError;
        }

        return {ids, names: new Set((permissions || []).map(permission => permission.name))};
    })().catch(error => {
        if(lockedPermissionsByOrg.get(orgID)?.request === request) lockedPermissionsByOrg.delete(orgID);
        ErrorHandler(Error(error.message), {organizationID: orgID, error}, `Something went wrong getting locked permissions for organization`)
        return {ids: new Set(), names: new Set()};
    });

    lockedPermissionsByOrg.set(orgID, {request, expiresAt: Date.now() + LOCKED_PERMISSIONS_CACHE_TIME});
    return request;
}

const invalidateLockedPermissions = (orgID) => {
    if(orgID) lockedPermissionsByOrg.delete(orgID);
}

const refreshPermissionConsumers = () => {
    queryClient.invalidateQueries({queryKey: QueryKeys.people.loggedInUser()});
    queryClient.invalidateQueries({queryKey: ['people']});
}

const getRoles = async (currentUser) => {
    const orgID = await getCurrentOrgID();

    // Resource counts come from the share join tables (BoardRoles/CalendarRoles/
    // FolderRoles) — cheap org-scoped RLS. Counting Boards/Calendars/Folders
    // directly re-evaluates their per-row access functions for every resource
    // in the org, and embedding RolePeople rows shipped every membership of
    // every role for data no list consumer reads (visibility uses
    // user.ManagingRoles) — together that made this fetch take seconds.
    const [{error, data}, lockedPermissionIDs] = await Promise.all([
        supabase.from('Roles').select('*, People:RolePeople(count), Permissions(id), BoardRoles(count), CalendarRoles(count), FolderRoles(count)').eq('organizationID', orgID).order("name", {ascending: true}),
        getLockedPermissions(orgID)
    ]);

    if(error){
        ErrorHandler(Error(error.message), {organizationID: orgID}, `Something went wrong getting roles for organization`)
        return [];
    }

    // Merge the org's locked flag onto each permission, then add hasLockedPermissions flag to each role
    const rolesWithLockedFlag = data.map(role => {
        const permissions = role.Permissions?.map(p => ({...p, locked: lockedPermissionIDs.ids.has(p.id)})) || [];
        return {
            ...role,
            Permissions: permissions,
            hasLockedPermissions: permissions.some(p => p.locked)
        };
    });

    // Hide hidden roles unless the viewer is an admin or a Role Owner (canManage) of that role
    return rolesWithLockedFlag.filter(role => isRoleVisibleToUser(role, currentUser));
}

const getRolesInFolders = async (currentUser) => {
    const orgID = await getCurrentOrgID();

    // Same lean select as getRoles — see the comment there for why resource
    // counts come from the share join tables and RolePeople rows are omitted.
    const [{ data: folderData }, {error, data}, lockedPermissionIDs] = await Promise.all([
        supabase.from('RoleFolders').select('*').eq('organizationID', orgID).order('name', {ascending: true}),
        supabase.from('Roles').select('*, People:RolePeople(count), Permissions(id), BoardRoles(count), CalendarRoles(count), FolderRoles(count)').eq('organizationID', orgID).order("name", {ascending: true}),
        getLockedPermissions(orgID)
    ]);

    if(error){
        ErrorHandler(Error(error.message), {organizationID: orgID}, `Something went wrong getting roles for organization`)
        return [];
    }

    // Merge the org's locked flag onto each permission, then add hasLockedPermissions flag to each role
    const rolesWithLockedFlag = data.map(role => {
        const permissions = role.Permissions?.map(p => ({...p, locked: lockedPermissionIDs.ids.has(p.id)})) || [];
        return {
            ...role,
            Permissions: permissions,
            hasLockedPermissions: permissions.some(p => p.locked)
        };
    });

    // Hide hidden roles unless the viewer is an admin or a Role Owner (canManage) of that role.
    // roles/index.jsx also enforces this client-side, but keep allRoles consistent everywhere.
    const visibleRoles = rolesWithLockedFlag.filter(role => isRoleVisibleToUser(role, currentUser));

    const structured = nestFolders(folderData, visibleRoles);

    return structured;
}

const getRoleFolder = async (id) => {
    // Embed isHidden so consumers can filter with isRoleVisibleToUser — the
    // embed is unfiltered and would otherwise leak hidden role names.
    const {error, data} = await supabase.from('RoleFolders')
        .select('*, Roles(id, name, isHidden)')
        .eq('id', id)
        .order('name', {referencedTable: 'Roles', ascending: true})
        .maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query:'getRoleFolder', id, error}, `Something went wrong getting that role folder`)
        throw Error()
    }

    return data
}

function nestFolders(folders, roles) {
    // Create a mapping of folder IDs to their corresponding folder object
    const folderMap = {};
    folders.forEach(folder => {
      folder.children = [];
      folder.isFolder = true;
      folder.Roles = roles.filter(g => g.folderID == folder.id);
      folderMap[folder.id] = folder;
    });
  
    // Create the root of the folder tree
    let root = [];
  
    folders.forEach(folder => {
      if (folder.parentFolderID === null || folder.parentFolderID === undefined) {
        root.push(folder);
      } else if (folderMap[folder.parentFolderID]) {
        folderMap[folder.parentFolderID].children.push(folder);
      }
    });

    return {children: root, Roles: roles.filter(g => g.folderID == null), allRoles: roles, allFolders: folders};
  }

const getPermissions = async () => {
    const orgID = await getCurrentOrgID();

    const [{error, data}, lockedPermissionIDs] = await Promise.all([
        supabase.from('Permissions').select('*, Roles(count)').order('operation', {ascending: true}),
        getLockedPermissions(orgID)
    ]);

    if(error){
        ErrorHandler(Error(error.message), {organizationID: orgID}, `Something went wrong getting permissions`)
        return [];
    }

    // Merge the org's per-org locked flag onto each permission
    return (data || []).map(p => ({...p, locked: lockedPermissionIDs.ids.has(p.id)}));
}


// Locks/unlocks a permission for the current org. Only the Account Owner can write
// OrganizationLockedPermissions rows (RLS enforced). Lock = insert row, unlock = delete row.
const setPermissionLocked = async ({permissionID, locked}) => {
    if(!permissionID){
        Toast.show({type: 'error', text2: 'Invalid permission ID'})
        return false;
    }

    const orgID = await getCurrentOrgID();

    const {error} = locked
        ? await supabase.from('OrganizationLockedPermissions').insert({organizationID: orgID, permissionID})
        : await supabase.from('OrganizationLockedPermissions').delete().eq('organizationID', orgID).eq('permissionID', permissionID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: 'setPermissionLocked', permissionID, locked, organizationID: orgID, error}, `Something went wrong ${locked ? 'locking' : 'unlocking'} that permission`)
        return false;
    }

    invalidateLockedPermissions(orgID);
    refreshPermissionConsumers();
    Toast.delayShow({type: 'success', text2: locked ? 'Permission locked' : 'Permission unlocked'});
    return true;
}


const getRole = async (id) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('Roles')
    .select(`*, People(${PEOPLE_SELECT}), RolePeople(*), Permissions(*), Boards(*, BoardRoles(*)), Calendars(*), Folders(*), QuickFilter:QuickFilters(*)`)
    .eq('id', id)
    .order('firstName', {referencedTable: 'People', ascending: true})
    .order('name', {referencedTable: 'Permissions', ascending: true})
    .order('name', {referencedTable: 'Boards', ascending: true})
    .order('name', {referencedTable: 'Calendars', ascending: true})
    .order('name', {referencedTable: 'Folders', ascending: true})
    .maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query: 'getRole', id, error}, `Something went wrong getting that role`)
        throw Error()
    }

    data.People = data.People?.map(p => {
        return {...p, canManage: data.RolePeople?.some(rp => rp.personID === p.id && rp.canManage)}
    })

    // Grant flags live on the BoardRoles join rows, not Boards — pull this
    // role's row out so resource lists can show "Admin · Post · ..." access.
    data.Boards = data.Boards?.map(({ BoardRoles, ...b }) => ({
        ...b,
        rolePerms: BoardRoles?.find(br => br.roleID === id) || null,
    }))

    // Merge the org's per-org locked flag onto each permission (Permissions.locked column is dropped)
    const lockedPermissionIDs = await getLockedPermissions(orgID);
    data.Permissions = data.Permissions?.map(p => ({...p, locked: lockedPermissionIDs.ids.has(p.id)}));

    return data;
}

const getPermission = async (id) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('Permissions')
        .select('*, Roles(*, People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey, RegisteredUsersOrganizations(isAdmin)))')
        .eq('id', id)
        .eq('Roles.People.RegisteredUsersOrganizations.organizationID', orgID)
        .order('name', {referencedTable: 'Roles', ascending: true})
        .maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query:'getPermission', id, error}, `Something went wrong getting that permission`)
        throw Error()
    }

    data.affectedUsers = data.Roles?.flatMap(r => 
        r.People?.sort((a,b) => (a.firstName > b.firstName) ? 1 : ((b.firstName > a.firstName) ? -1 : 0)).map(p => { 
            return {...p, Roles: [r.name]}
        })
    )

    if(data.affectedUsers && data.affectedUsers.length > 0){
        const individuals = {}
        data.affectedUsers.forEach(a => {
            if(!individuals[a.id]){
                individuals[a.id] = a
            }
            else{
                individuals[a.id].Roles.push(a.Roles[0])
            }
        })

        data.affectedUsers = Object.values(individuals).sort((a,b) => (a.firstName > b.firstName) ? 1 : ((b.firstName > a.firstName) ? -1 : 0))
    }
    else{
        data.affectedUsers = [];
    }

    // Merge the org's per-org locked flag (Permissions.locked column is dropped)
    const lockedPermissionIDs = await getLockedPermissions(orgID);
    data.locked = lockedPermissionIDs.ids.has(data.id);

    return data

}



const saveRole = async ({details, originalValues, membersOnly, canEditPermissions, canEditLockedPermissions, canEditMembers}) => {
    const orgID = await getCurrentOrgID();

    const role = {
        id: details.id,
        organizationID: orgID,
        name: details.name?.trim(),
        description: details.description?.trim(),
        isHidden: details.isHidden,
        isAccessControl: details.isAccessControl || false,
        folderID: details.folderID,
        quickFilterID: details.quickFilterID
    }

    // Filter out locked permissions unless the caller is the account owner,
    // who is permitted by RLS to assign locked permissions to roles.
    const safePermissions = canEditLockedPermissions
        ? (details.Permissions || [])
        : (details.Permissions?.filter(p => !p.locked) || []);

    if(role.id){
        return updateRole(role, details.People, safePermissions, membersOnly, originalValues, canEditPermissions, canEditLockedPermissions, canEditMembers);
    }
    else{
        return createRole(role, details.People, safePermissions, canEditLockedPermissions);
    }
}


const createRole = async (role, members = [], permissions = [], canEditLockedPermissions = false) => {
    // Double-check: filter out any locked permissions as a safety measure,
    // unless the caller is the account owner.
    const safePermissions = canEditLockedPermissions
        ? (permissions || [])
        : (permissions?.filter(p => !p.locked) || []);

    const {error, data} = await supabase.from('Roles').insert(role).select().single();

    if(error){
        ErrorHandler(Error(error.message), {role, members, permissions: safePermissions}, `Something went wrong creating the Role "${role.name}"`)
        throw Error()
    }

    // Dynamic roles derive members from their filter — no RolePeople rows.
    const memberInserts = role.quickFilterID ? [] : (members || []);
    const {error: errorMembers, data: dataMembers} = memberInserts.length
        ? await supabase.from('RolePeople').insert(memberInserts.map(m => ({organizationID: role.organizationID, roleID: data.id, personID: m.id, canManage: m.canManage || false})))
        : {error: null, data: []};

    if(errorMembers){
        ErrorHandler(Error(errorMembers.message), {role, members, permissions}, `Something went wrong creating the members for the Role "${role.name}"`)
        throw Error()
    }

    const {error: errorPermissions, data: dataPermissions} = await supabase.from('RolePermissions').insert(safePermissions?.map(p => ({organizationID: role.organizationID, roleID: data.id, permissionID: p.id})));

    if(errorPermissions){
        ErrorHandler(Error(errorPermissions.message), {role, members, permissions: safePermissions}, `Something went wrong creating the permissions for the Role "${role.name}"`)
        throw Error()
    }

    Toast.delayShow({type: 'success',text2: `New role "${role.name}" Created!`});

    insertActivity({
        subjectType: 'Role',
        subjectID: data.id,
        subjectName: role.name,
        verb: 'create',
    }).catch(() => {});

    return {...data, People: dataMembers, Permissions: dataPermissions};
}



const updateRole = async (role, members = [], permissions = [], membersOnly, originalValues = null, canEditPermissions = false, canEditLockedPermissions = false, canEditMembers = false) => {
    // Double-check: filter out any locked permissions as a safety measure,
    // unless the caller is the account owner.
    const safePermissions = canEditLockedPermissions
        ? (permissions || [])
        : (permissions?.filter(p => !p.locked) || []);

    let errorPermissions, errorPermissionsDelete, errorMembers, errorMembersDelete;
    let permissionsToAdd = [], permissionsToDelete = [], safePermissionsToAdd = [], safePermissionsToDelete = [];
    let membersToAdd = [], membersToDelete = [];

    const {error, data} = membersOnly 
        ? await supabase.from('Roles').select('*, RolePeople(*, Person:People(id)), Permissions(*)').eq('id', role.id).single()
        : await supabase.from('Roles').update(role).eq('id', role.id).select('*, RolePeople(*, Person:People(id)), Permissions(*)').single();


    // Only diff/write RolePeople when the user can actually manage members.
    // Mirrors the RolePeople RLS policy (admin / Role Owner / Edit Roles) so a
    // non-admin viewer saving the role doesn't trigger RLS denials.
    const writePromises = [];

    if(canEditMembers){
        // Dynamic roles derive members from their filter — purge stale member
        // rows instead of diffing, but keep Role Owner (canManage) rows.
        const effectiveMembers = role.quickFilterID ? [] : members;
        const existingMembers = (data.RolePeople || [])
            .filter(rp => rp.Person && (role.quickFilterID ? !rp.canManage : true))
            .map(rp => rp.Person);
        const { itemsToAdd: membersToAddComputed, itemsToDelete: membersToDeleteComputed, unchangedItems: membersToUpdate } = findAssociativeChanges(effectiveMembers, existingMembers);
        membersToAdd = membersToAddComputed;
        membersToDelete = membersToDeleteComputed;

        if(membersToAdd.length > 0){
            writePromises.push(
                supabase.from('RolePeople').insert(membersToAdd.map(m => ({organizationID: role.organizationID, roleID: role.id, personID: m.id, canManage: m.canManage || false})))
                    .then(({error}) => { if(error) errorMembers = error })
            );
        }

        if(membersToDelete.length > 0){
            writePromises.push(
                supabase.from('RolePeople').delete().in('personID', membersToDelete.map(m => m.id)).eq('roleID', role.id)
                    .then(({error}) => { if(error) errorMembersDelete = error })
            );
        }

        // Fix: forEach(async ...) was fire-and-forget — errors swallowed, data stale.
        // Now properly awaited via Promise.all.
        const memberUpdatePromises = membersToUpdate
            .map(m => {
                const newValue = m.canManage
                const oldValue = data.RolePeople?.find(rp => rp.personID == m.id)?.canManage
                if(newValue !== oldValue){
                    return supabase.from('RolePeople').update({canManage: newValue}).eq('personID', m.id).eq('roleID', role.id);
                }
                return null;
            })
            .filter(Boolean);
        writePromises.push(...memberUpdatePromises);
    }


    if(!membersOnly){
        data.People = data.RolePeople?.map(p => ({...p.Person, ...p}));
    }

    if(canEditPermissions){
        ({ itemsToAdd: permissionsToAdd, itemsToDelete: permissionsToDelete } = findAssociativeChanges(safePermissions, data.Permissions));

        // Filter out locked permissions from being added/deleted (extra safety),
        // unless the caller is the account owner.
        safePermissionsToAdd = canEditLockedPermissions ? permissionsToAdd : permissionsToAdd.filter(p => !p.locked);
        safePermissionsToDelete = canEditLockedPermissions ? permissionsToDelete : permissionsToDelete.filter(p => !p.locked);

        if(safePermissionsToAdd.length > 0){
            writePromises.push(
                supabase.from('RolePermissions').insert(safePermissionsToAdd.map(p => ({organizationID: role.organizationID, roleID: role.id, permissionID: p.id})))
                    .then(({error}) => { if(error) errorPermissions = error })
            );
        }

        if(safePermissionsToDelete.length > 0){
            writePromises.push(
                supabase.from('RolePermissions').delete().in('permissionID', safePermissionsToDelete.map(p => p.id)).eq('roleID', role.id)
                    .then(({error}) => { if(error) errorPermissionsDelete = error })
            );
        }
    }

    // Run all independent writes in parallel instead of sequentially.
    // Before: up to 5 sequential HTTP round-trips (~5s). Now: 1 round-trip for
    // the role update + 1 parallel batch for all association writes.
    await Promise.all(writePromises);

    if(error || errorMembers || errorMembersDelete || errorPermissions || errorPermissionsDelete){
        ErrorHandler(Error("Errors updating Role people or Role permissions"), {role, members, permissions, error, errorMembers, errorMembersDelete, errorPermissions, errorPermissionsDelete}, `Something went wrong updating the Role "${role.name}"`)
    }

    Toast.delayShow({type: 'success',text2: `Role "${role.name}" Updated!`});

    // Build activity details
    const scalarDetails = !membersOnly && originalValues
        ? buildUpdateDetails(originalValues, role, {
            name: { label: 'Name' },
            description: { label: 'Description' },
            isHidden: { label: 'Hidden' },
            isAccessControl: { label: 'Access Control' },
        })
        : null;

    const memberDetails = [
        ...membersToAdd.map(m => `- Added member '${`${m.firstName ?? ''} ${m.lastName ?? ''}`.trim()}'`),
        ...membersToDelete.map(m => `- Removed member '${`${m.firstName ?? ''} ${m.lastName ?? ''}`.trim()}'`),
    ].filter(Boolean);

    const permissionDetails = canEditPermissions ? [
        ...(safePermissionsToAdd || []).map(p => `- Added permission '${p.name}'`),
        ...(safePermissionsToDelete || []).map(p => `- Removed permission '${p.name}'`),
    ] : [];

    const details = joinDetails(scalarDetails, memberDetails, permissionDetails);
    insertActivity({
        subjectType: 'Role',
        subjectID: role.id,
        subjectName: role.name,
        verb: 'update',
        details,
    }).catch(() => {});

    return data;
}


const deleteRole = async ({roleID}) => {
    if(!roleID){
        Toast.show({type: 'error', text2: 'Invalid role ID'})
        throw Error('Invalid role ID')
    }

    const {data: existing} = await supabase.from('Roles').select('name').eq('id', roleID).maybeSingle();

    const {error} = await supabase.from('Roles').delete().eq('id', roleID);

    if(error){
        ErrorHandler(Error(error.message), {roleID, error}, `Something went wrong deleting that Role`)
        throw Error()
    }

    insertActivity({
        subjectType: 'Role',
        subjectID: roleID,
        subjectName: existing?.name,
        verb: 'delete',
    }).catch(() => {});
}

const saveRoleFolder = async (details) => {
    if(details.id){
        return updateRoleFolder(details);
    }
    else{
        return createRoleFolder(details);
    }
}

const createRoleFolder = async (details) => {
    const orgID = await getCurrentOrgID();

    const newFolder = {
        name: details.name,
        organizationID: orgID,
        parentFolderID: details.parentFolderID
    }

    const {error, data} = await supabase.from('RoleFolders').insert(newFolder).select().single();

    if(error){
        ErrorHandler(Error(error.message), {newFolder, error}, `Something went wrong creating that Role folder`)
        throw Error()
    }

    if(details.Roles?.length > 0){
        const {error} = await supabase.from("Roles").update({folderID: data.id}).in('id', details.Roles?.map(g => g.id));

        if(error){
            ErrorHandler(Error(error.message), {details, error}, `Something went wrong updating that Role folder`)
            throw Error()
        }
    }

    Toast.delayShow({type: 'success',text2: `New Role folder "${details.name}" created successfully!`});
    return data;
}


const updateRoleFolder = async (details) => {
    const update = {
        name: details.name,
        parentFolderID: details.parentFolderID
    }

    const { data, error } = await supabase.from("RoleFolders").update(update).eq("id", details.id).select('*, Roles!inner(id)').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateRoleFolder", input: update, ...error}, `Something went wrong updating that Role folder`)
        return false;
    }

    if(details.Roles?.length > 0){
        if(data){
            const existingIDsToRemove = data.Roles.map(r => r.id).filter(id => !details.Roles?.map(g => g.id).includes(id))

            if(existingIDsToRemove.length > 0){
                const {error} = await supabase.from("Roles").update({folderID: null}).in('id', existingIDsToRemove);

                if(error){
                    ErrorHandler(Error(error.message), {details, error})
                    throw Error()
                }
            }
        }

        const {error} = await supabase.from("Roles").update({folderID: details.id}).in('id', details.Roles?.map(g => g.id));

        if(error){
            ErrorHandler(Error(error.message), {details, error}, `Something went wrong updating that Role folder`)
            throw Error()
        }
    }

    Toast.delayShow({type: 'success',text2: `This role folder has been updated successfully!`});
    return data;
}


const deleteRoleFolder = async (id) => {
    const { error } = await supabase.from("RoleFolders").delete().eq("id", id);

    if(error){
        ErrorHandler(Error(error.message), {id, error}, `Something went wrong deleting that Role folder`)
        throw Error()
    }
}

const addPeopleToRoles = async (personIDs, roleIDs) => {
    if (!personIDs?.length || !roleIDs?.length) return;

    const orgID = await getCurrentOrgID();

    // Dynamic roles derive members from their filter — they can't be assigned manually.
    const { data: staticRoles, error: roleFetchError } = await supabase
        .from('Roles')
        .select('id')
        .in('id', roleIDs)
        .is('quickFilterID', null);

    if (roleFetchError) {
        ErrorHandler(Error(roleFetchError.message), { roleIDs, error: roleFetchError }, 'Something went wrong adding people to roles');
        throw Error(roleFetchError.message);
    }

    const staticRoleIDs = (staticRoles || []).map(r => r.id);
    if (staticRoleIDs.length === 0) return { added: 0, skipped: 0 };

    // Fetch existing RolePeople entries for these person/role combos to avoid duplicates
    const { data: existing, error: fetchError } = await supabase
        .from('RolePeople')
        .select('personID, roleID')
        .in('personID', personIDs)
        .in('roleID', staticRoleIDs);

    if (fetchError) {
        ErrorHandler(Error(fetchError.message), { personIDs, roleIDs, error: fetchError }, 'Something went wrong adding people to roles');
        throw Error(fetchError.message);
    }

    const existingSet = new Set((existing || []).map(rp => `${rp.personID}:${rp.roleID}`));

    const toInsert = [];
    for (const personID of personIDs) {
        for (const roleID of staticRoleIDs) {
            if (!existingSet.has(`${personID}:${roleID}`)) {
                toInsert.push({ organizationID: orgID, roleID, personID, canManage: false });
            }
        }
    }

    if (toInsert.length === 0) return { added: 0, skipped: existingSet.size };

    const { error: insertError } = await supabase.from('RolePeople').insert(toInsert);

    if (insertError) {
        ErrorHandler(Error(insertError.message), { toInsert, error: insertError }, 'Something went wrong adding people to roles');
        throw Error(insertError.message);
    }

    return { added: toInsert.length, skipped: existingSet.size };
};

export {
    getLockedPermissions,
    getRoles,
    getRolesInFolders,
    getRoleFolder,
    getRole,
    getPermissions,
    getPermission,
    setPermissionLocked,
    saveRole,
    createRole,
    updateRole,
    deleteRole,
    saveRoleFolder,
    deleteRoleFolder,
    addPeopleToRoles
}